import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import { clamp } from 'lodash-es';
import type { DateTime } from 'luxon';
import React, { useCallback, useMemo, useRef } from 'react';
import styles from '../GanttChart.module.scss';
import type { GanttCellParams } from '../gantt-types';
import type { GanttZoomLevel } from '../gantt-zoom';

interface Props {
  zoom: GanttZoomLevel;
  dayColumns: Array<DateTime>;
  scrollLeft: number;
  width: number;
}

interface Column {
  date: DateTime;
  width: number;
  offset: number;
}

const GanttChartYearGrid: React.FC<Props> = ({ zoom, dayColumns, scrollLeft }) => {
  const gridRef = useRef<HTMLDivElement>(null);

  const yearColumns: Array<Column> = useMemo(() => {
    const columns: Array<Column> = [];
    let totalWidth = 0;
    for (let index = 0; index < dayColumns.length; index++) {
      const date = dayColumns[index];
      const span = date.daysInYear - date.diff(date.startOf('year'), 'days').days;
      const spanOffset = span - 1;
      const width = span * zoom.columnWidth;
      columns.push({
        date,
        width,
        offset: totalWidth,
      });
      totalWidth += width;
      index += spanOffset;
    }
    return columns;
  }, [dayColumns, zoom]);

  const getYearColumnWidth = useCallback(
    (params: { index: number }) => yearColumns[params.index].width,
    [yearColumns]
  );

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: yearColumns.length,
    getScrollElement: () => gridRef?.current,
    estimateSize: index => getYearColumnWidth({ index }),
  });

  React.useLayoutEffect(() => {
    columnVirtualizer.scrollToOffset(scrollLeft);
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  }, [scrollLeft]);
  // Keep track of month label widths so that we can make them sticky during horizontal scroll.
  const yearLabelWidths = useRef<Record<number, number>>({});
  const setYearLabelWidth = useCallback((columnIndex: number, element: HTMLSpanElement | null) => {
    yearLabelWidths.current[columnIndex] = element?.offsetWidth ?? 0;
  }, []);

  const renderCell = useCallback(
    ({ columnIndex, key }: GanttCellParams) => {
      const column = yearColumns[columnIndex];

      const label = String(column.date.year);

      const labelWidth = yearLabelWidths.current[columnIndex] ?? 0;
      const PADDING = 16;
      const left = clamp(scrollLeft - column.offset, 0, column.width - labelWidth - PADDING * 2);
      return (
        <div
          className={classNames(styles.topHeaderYearCell, {
            [styles.firstCell]: columnIndex === 0,
          })}
          key={key}
          style={{ paddingLeft: PADDING }}
        >
          <div className={styles.label} style={{ left }}>
            <span ref={el => setYearLabelWidth(columnIndex, el)}>{label}</span>
          </div>
        </div>
      );
    },
    [yearColumns, scrollLeft, yearLabelWidths, setYearLabelWidth]
  );

  return (
    <div className={styles.topHeaderYearRow} ref={gridRef}>
      <div
        style={{
          width: columnVirtualizer.getTotalSize(),
          height: '100%',
          position: 'relative',
        }}
      >
        {columnVirtualizer.getVirtualItems().map(virtualColumn => (
          <div
            key={virtualColumn.key}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              transform: `translateX(${virtualColumn.start}px)`,
            }}
          >
            <div
              style={{
                width: getYearColumnWidth({ index: virtualColumn.index }),
                height: 24,
                overflow: 'hidden',
              }}
            >
              {renderCell({
                columnIndex: virtualColumn.index,
                key: virtualColumn.key,
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
export default GanttChartYearGrid;
